import { useEffect, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { sendInvoiceWhatsApp } from "../../utils/sendInvoiceWhatsApp";
import { FaWhatsapp } from "react-icons/fa";

import {
  getPayments,
  deletePayment,
  updatePaymentStatus,
} from "../../services/paymentService";

import { getMembers } from "../../services/memberService";

import "./Payments.css";

export default function Payments() {
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================================================
  // LOAD PAYMENTS + MEMBERS
  // =========================================================

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);

      const [paymentData, memberData] = await Promise.all([
        getPayments(),
        getMembers(),
      ]);

      setPayments(paymentData);
      setMembers(memberData);
    } catch (error) {
      console.error("Payments loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FIND MEMBER
  // =========================================================

  const getMemberForPayment = (payment) => {
    // First priority: Member ID
    if (payment.memberId) {
      const memberById = members.find(
        (member) => member.memberId === payment.memberId,
      );

      if (memberById) {
        return memberById;
      }
    }

    // Fallback for old payment records
    // that don't have memberId
    if (payment.memberName) {
      const memberByName = members.find(
        (member) =>
          member.fullName?.toLowerCase() === payment.memberName?.toLowerCase(),
      );

      if (memberByName) {
        return memberByName;
      }
    }

    return null;
  };

  // =========================================================
  // DELETE PAYMENT
  // =========================================================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this payment?",
    );

    if (!confirmDelete) return;

    try {
      await deletePayment(id);
      await loadPayments();
    } catch (error) {
      console.error("Delete payment error:", error);
      alert("Failed to delete payment.");
    }
  };

  // =========================================================
  // UPDATE PAYMENT STATUS
  // =========================================================

  const handleStatusChange = async (id, status) => {
    try {
      await updatePaymentStatus(id, status);
      await loadPayments();
    } catch (error) {
      console.error("Payment status update error:", error);
      alert("Failed to update payment status.");
    }
  };

  // =========================================================
  // STATISTICS
  // =========================================================

  const totalPayments = payments.length;

  const paidPayments = payments.filter((payment) => payment.status === "Paid");

  const pendingPayments = payments.filter(
    (payment) => payment.status === "Pending",
  );

  const failedPayments = payments.filter(
    (payment) => payment.status === "Failed",
  );

  const totalRevenue = paidPayments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0,
  );

  // =========================================================
  // CURRENCY FORMAT
  // =========================================================

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  // =========================================================
  // RENDER
  // =========================================================

  const handleSendWhatsApp = (payment) => {
    try {
      const member = getMemberForPayment(payment);

      if (!member) {
        alert("Member details not found.");
        return;
      }

      sendInvoiceWhatsApp(member, payment);
    } catch (error) {
      console.error("WhatsApp Error:", error);

      alert(error.message || "Unable to open WhatsApp.");
    }
  };
  return (
    <DashboardLayout>
      <div className="payments-page">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="payments-header">
          <div>
            <h1>Payments Management</h1>

            <p
              style={{
                fontWeight: "bold",
                color: "white",
              }}
            >
              Track membership payments, revenue and transaction status.
            </p>
          </div>

          <button
            className="add-payment-btn"
            onClick={() => navigate("/payments/add")}
          >
            + Add Payment
          </button>
        </div>

        {/* =====================================================
            STATISTICS
        ===================================================== */}

        <div className="payments-stats">
          <div className="payment-stat-card">
            <h4>Total Payments</h4>
            <h2>{totalPayments}</h2>
          </div>

          <div className="payment-stat-card paid-card">
            <h4>Paid</h4>
            <h2>{paidPayments.length}</h2>
          </div>

          <div className="payment-stat-card pending-card">
            <h4>Pending</h4>
            <h2>{pendingPayments.length}</h2>
          </div>

          <div className="payment-stat-card revenue-card">
            <h4>Total Revenue</h4>

            <h2>₹{formatCurrency(totalRevenue)}</h2>
          </div>
        </div>

        {/* =====================================================
            OPTIONAL SUMMARY
        ===================================================== */}

        <div className="payment-summary">
          <span>
            Total Transactions: <strong>{totalPayments}</strong>
          </span>

          <span>
            Paid: <strong>{paidPayments.length}</strong>
          </span>

          <span>
            Pending: <strong>{pendingPayments.length}</strong>
          </span>

          <span>
            Failed: <strong>{failedPayments.length}</strong>
          </span>
        </div>

        {/* =====================================================
            TABLE
        ===================================================== */}

        <div className="payments-table-card">
          <table className="member-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Member ID</th>
                <th>Member</th>
                <th>Membership Plan</th>
                <th>Amount</th>
                <th>Payment Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      textAlign: "center",
                      padding: "30px",
                    }}
                  >
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length > 0 ? (
                payments.map((payment) => {
                  const member = getMemberForPayment(payment);

                  const memberPhoto =
                    member?.photo ||
                    member?.photoBase64 ||
                    payment?.memberPhoto ||
                    "";

                  const memberId = member?.memberId || payment?.memberId || "-";

                  const memberName =
                    member?.fullName || payment?.memberName || "-";

                  return (
                    <tr key={payment.id}>
                      {/* =================================================
                          PHOTO
                      ================================================= */}

                      <td>
                        {memberPhoto ? (
                          <img
                            src={memberPhoto}
                            alt={memberName}
                            className="payment-member-photo"
                          />
                        ) : (
                          <div className="payment-photo-placeholder">
                            {memberName?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                        )}
                      </td>

                      {/* =================================================
                          MEMBER ID
                      ================================================= */}

                      <td>
                        <span className="payment-member-id">{memberId}</span>
                      </td>

                      {/* =================================================
                          MEMBER NAME
                      ================================================= */}

                      <td>
                        <strong>{memberName}</strong>
                      </td>

                      {/* =================================================
                          PLAN
                      ================================================= */}

                      <td>
                        <span className="payment-plan-pill">
                          {payment.membershipPlan || "-"}
                        </span>
                      </td>

                      {/* =================================================
                          AMOUNT
                      ================================================= */}

                      <td>
                        <strong className="payment-amount">
                          ₹{formatCurrency(payment.amount)}
                        </strong>
                      </td>

                      {/* =================================================
                          DATE
                      ================================================= */}

                      <td>{payment.paymentDate || "-"}</td>

                      {/* =================================================
                          STATUS
                      ================================================= */}

                      <td>
                        <select
                          className={`status-select ${
                            payment.status === "Paid"
                              ? "status-paid"
                              : payment.status === "Pending"
                                ? "status-pending"
                                : "status-failed"
                          }`}
                          value={payment.status}
                          onChange={(e) =>
                            handleStatusChange(payment.id, e.target.value)
                          }
                        >
                          <option value="Paid">Paid</option>

                          <option value="Pending">Pending</option>

                          <option value="Failed">Failed</option>
                        </select>
                      </td>

                      {/* =================================================
                          DELETE
                      ================================================= */}

                      <td className="payment-actions">
                        {payment.status === "Paid" && (
                          <button
                            className="invoice-whatsapp-btn"
                            onClick={() => handleSendWhatsApp(payment)}
                            title="Send payment details on WhatsApp"
                          >
                            <FaWhatsapp />
                          </button>
                        )}

                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(payment.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      textAlign: "center",
                      padding: "30px",
                    }}
                  >
                    No Payments Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
